import { reflect } from '@effector/reflect';
import { Anchor } from '@mantine/core';
import { Link } from 'atomic-router-react';
import clsx from 'clsx';
import { StoreValue } from 'effector';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceDetailed } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { accountRoute, cashdeskRoute, deviceRoute, gameSettingsRoute } from 'shared/routing';
import { createRegistry, MultiSelect, RegistryRootProps, SearchInput } from 'shared/ui';
import { DeviceRegistryModel } from '../../model';
import cls from './device-registry.module.scss';

export type DeviceRegistryProps = RegistryRootProps & {
	withTitle?: boolean;
	withAccount?: boolean;
	withCashdesk?: boolean;
	withCashdeskFilter?: boolean;
	tableTdActionSlot?: (device: DeviceDetailed) => ReactNode;
};

export const createDeviceRegistry = ({
	model,
	$cashdesks,
	$games,
	$currencies,
}: DeviceRegistryModel) => {
	const Registry = createRegistry(model);

	const NameFilter = reflect({
		view: SearchInput<'input'>,
		bind: {
			'data-test-id': 'device-registry-name-filter',
			label: translated('device-registry.filters.name.label'),
			placeholder: translated('device-registry.filters.name.placeholder'),
			className: cls.deviceRegistryFilter,
			value: model.filters.name.$value,
			onChange: (e) => model.filters.name.setValue(e.target.value),
		},
	});

	const PinFilter = reflect({
		view: SearchInput<'input'>,
		bind: {
			'data-test-id': 'device-registry-pin-filter',
			label: translated('device-registry.filters.pin.label'),
			placeholder: translated('device-registry.filters.pin.placeholder'),
			className: cls.deviceRegistryFilter,
			value: model.filters.cashdeskCode.$value,
			onChange: (e) => model.filters.cashdeskCode.setValue(e.target.value),
		},
	});

	const CashdeskFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'device-registry-cashdesk-filter',
			label: translated('device-registry.filters.cashdesk.label'),
			placeholder: translated('device-registry.filters.cashdesk.placeholder'),
			nothingFoundMessage: translated('device-registry.filters.cashdesk.empty'),
			className: cls.deviceRegistryFilter,
			data: $cashdesks,
			value: model.filters.cashdeskIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => model.filters.cashdeskIds.setValue(value.map(Number)),
		},
	});

	const GamesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'device-registry-games-filter',
			label: translated('device-registry.filters.games.label'),
			placeholder: translated('device-registry.filters.games.placeholder'),
			nothingFoundMessage: translated('device-registry.filters.games.empty'),
			className: cls.deviceRegistryFilter,
			data: $games,
			value: model.filters.gameIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => model.filters.gameIds.setValue(value.map(Number)),
		},
	});

	const CurrenciesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'device-registry-currencies-filter',
			label: translated('device-registry.filters.currencies.label'),
			placeholder: translated('device-registry.filters.currencies.placeholder'),
			nothingFoundMessage: translated('device-registry.filters.currencies.empty'),
			className: cls.deviceRegistryFilter,
			data: $currencies,
			value: model.filters.currencies.$value,
			onChange: (value) => model.filters.currencies.setValue(value),
			searchable: true,
		},
	});

	const StatusesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'device-registry-statuses-filter',
			label: translated('device-registry.filters.statuses.label'),
			placeholder: translated('device-registry.filters.statuses.placeholder'),
			nothingFoundMessage: translated('device-registry.filters.statuses.empty'),
			className: cls.deviceRegistryFilter,
			data: $t.map((t) =>
				[true, false].map((value) => ({
					value: value.toString(),
					label: t(`device-registry.statuses.${value ? 'active' : 'not-active'}`),
				})),
			),
			searchable: false,
			value: model.filters.isActive.$value.map((values) => values.map(String)),
			onChange: (value) => model.filters.isActive.setValue(value.map((v) => v === 'true')),
		},
	});

	const DeviceLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof deviceRoute.$params>>>,
		bind: {
			'data-test-id': 'device-registry-device-link',
			to: deviceRoute,
			component: Link,
		},
	});

	const AccountLink = reflect({
		view: Anchor<typeof Link>,
		bind: {
			'data-test-id': 'device-registry-account-link',
			to: accountRoute,
			component: Link,
		},
	});

	const CashdeskLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof cashdeskRoute.$params>>>,
		bind: {
			'data-test-id': 'device-registry-cashdesk-link',
			to: cashdeskRoute,
			component: Link,
		},
	});

	const GameLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof gameSettingsRoute.$params>>>,
		bind: {
			'data-test-id': 'device-registry-game-link',
			to: gameSettingsRoute,
			component: Link,
		},
	});

	const DeviceRegistry = forwardRef<HTMLDivElement, DeviceRegistryProps>(
		(
			{
				withTitle = true,
				withAccount = true,
				withCashdesk = true,
				withCashdeskFilter = true,
				className,
				tableTdActionSlot,
				...props
			},
			ref,
		) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
					data-with-cashdesk-filter={withCashdeskFilter || undefined}
					className={clsx(cls.deviceRegistry, className)}
				>
					{withTitle && <Registry.Title>{t('device-registry.title')}</Registry.Title>}
					<Registry.TopBar className={cls.deviceRegistryTopBar}>
						<Registry.Filters
							classNames={{
								root: cls.deviceRegistryFilters,
								inner: cls.deviceRegistryFiltersInner,
							}}
						>
							<NameFilter />
							<PinFilter />
							{withCashdeskFilter && <CashdeskFilter />}
							<GamesFilter />
							<CurrenciesFilter />
							<StatusesFilter />
						</Registry.Filters>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							{withAccount && <Registry.Table.Col />}
							{withCashdesk && <Registry.Table.Col />}
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='name'>
									{t('device-registry.table-columns.name')}
								</Registry.Table.ThSorter>
								{withAccount && (
									<Registry.Table.ThSorter field='account'>
										{t('device-registry.table-columns.account')}
									</Registry.Table.ThSorter>
								)}
								{withCashdesk && (
									<Registry.Table.ThSorter field='cashdesk'>
										{t('device-registry.table-columns.cashdesk')}
									</Registry.Table.ThSorter>
								)}
								<Registry.Table.ThSorter field='game'>
									{t('device-registry.table-columns.game')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='currency'>
									{t('device-registry.table-columns.currency')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='cashdeskCode'>
									{t('device-registry.table-columns.pin')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='players'>
									{t('device-registry.table-columns.players')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='isActive'>
									{t('device-registry.table-columns.status')}
								</Registry.Table.ThSorter>
								<Registry.Table.Th>{t('device-registry.table-columns.actions')}</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList>
								{(row) => (
									<>
										<Registry.Table.Td>
											<DeviceLink params={{ id: row.id }}>{row.name}</DeviceLink>
										</Registry.Table.Td>
										{withAccount && (
											<Registry.Table.Td>
												<AccountLink query={{ id: row.account.id }}>{row.account.name}</AccountLink>
											</Registry.Table.Td>
										)}
										{withCashdesk && (
											<Registry.Table.Td>
												<CashdeskLink params={{ id: row.cashdesk.id }}>
													{row.cashdesk.name}
												</CashdeskLink>
											</Registry.Table.Td>
										)}
										<Registry.Table.Td>
											<GameLink params={{ id: row.game.id }}>{row.game.title}</GameLink>
										</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>{row.cashdeskCode}</Registry.Table.Td>
										<Registry.Table.Td>{row.players}</Registry.Table.Td>
										<Registry.Table.Td>
											{row.isActive
												? t(`device-registry.statuses.active`)
												: t(`device-registry.statuses.not-active`)}
										</Registry.Table.Td>
										<Registry.Table.Td>{tableTdActionSlot?.(row)}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('device-registry.empty.title')}
						message={t('device-registry.empty.message')}
					/>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('device-registry.table-empty.title')}
						message={t('device-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return DeviceRegistry;
};
